import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import ReCAPTCHA from "react-google-recaptcha";
import { FieldValues, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

import FooterModal from "@components/FooterModal/FooterModal";
import CustomForm from "@components/shared/CustomForm/CustomForm";
import Loader from "@components/shared/Loader/Loader";
import Modals from "@components/shared/Modal/Modal";
import PageTitle from "@components/shared/PageTitle/PageTitle";
import { AVAILIO_LOGO } from "@constants/assets";
import { CONSTANTS } from "@constants/index";
import { CONTACT_TYPE } from "@constants/register";
import useRegister from "@hooks/useRegister/useRegister";
import {
  ErrorData,
  UserApiRequest,
  UserFormData,
} from "@models/components/auth.model";
import handleResponse from "@services/apiConfig/api.response.service";
import API_SERVICES from "@services/index";
import authSlice from "@slices/auth.slice";
import modalSlice from "@slices/modal.slice";
import { RootState } from "@store/index";
import commonUtils from "@utils/common.utils";

const initialState: UserFormData = {
  city: "",
  companyName: "",
  contactType: CONTACT_TYPE.SINGLE,
  country: "",
  dob: "",
  email: "",
  firstName: "",
  gender: "",
  language: "",
  lastName: "",
  salutation: "",
  street: "",
  telephoneNumber: "",
  zipcode: "",
};

const RegisterModal: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const recaptchaV3Ref = useRef<ReCAPTCHA | null>(null);
  const recaptchaV2Ref = useRef<ReCAPTCHA | null>(null);
  const [useV2Recaptcha, setUseV2Recaptcha] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const url = location.href;
  const [formKey, setFormKey] = useState(0);
  const invitationToken = url.includes("verify-invitation")
    ? url.split("/").pop()
    : "";

  const [isInvitation, setIsInvitation] = useState(false);
  const [invitationData, setInvitationData] =
    useState<UserFormData>(initialState);

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    register,
    setValue,
    watch,
  } = useForm<FieldValues>({
    defaultValues: invitationData,
    mode: "onTouched",
    values: invitationData,
  });

  const { activeFields, registerOptions, updateFields } = useRegister(
    watch("contactType")
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { invitationProfileId } = useSelector((state: RootState) => state.auth);
  useEffect(() => {
    updateFields(watch("contactType"));
  }, [watch("contactType")]);
  useEffect(() => {
    if (invitationToken != "") {
      getRegistrationInvitation();
    }
  }, [invitationToken]);

  const getRegistrationInvitation = async () => {
    const response =
      await API_SERVICES.USER.REGISTRATION_INVITATION(invitationProfileId);
    const responseData = handleResponse(response);
    if (responseData?.data) {
      setIsInvitation(true);
      const invitationData = responseData?.data.profile;
      const email = responseData?.data.email;
      const dob = invitationData?.dob
        ? moment(invitationData?.dob).format(
            CONSTANTS.DATE_FORMATS.DAY_MONTH_YEAR_FORMAT
          )
        : "";
      const telephoneNumber =
        invitationData.telephoneNumber != null &&
        invitationData.telephoneNumber.indexOf("+") === 0
          ? invitationData.telephoneNumber
          : "+" + invitationData.telephoneNumber;
      const country = invitationData.country || "";
      const fullData = {
        ...initialState,
        ...invitationData,
        country,
        dob,
        email,
        telephoneNumber,
      };

      setInvitationData(fullData);
      setTimeout(() => {
        setFormKey(k => k + 1);
      }, 1000);
    }
  };

  const submitForm: SubmitHandler<UserFormData> = async data => {
    Object.keys(data).forEach(k => {
      const key = k as keyof UserFormData;
      if (typeof data[key] === "string") {
        data[key] = data[key] ? data[key]!.trim() : "";
      }
    });

    if (isValid) {
      setIsLoading(true);

      let tempToken = token;

      if (!useV2Recaptcha) {
        tempToken = (await recaptchaV3Ref.current?.executeAsync()) ?? null;
        setToken(tempToken);
      }

      if (!tempToken) {
        if (!useV2Recaptcha) {
          setUseV2Recaptcha(true);
        } else {
          console.error("reCAPTCHA verification failed");
        }
        setIsLoading(false);
        return;
      }

      const commonContactType = {
        city: data.city,
        companyName: data.companyName,
        contactType: data.contactType,
        country: data.country,
        language: data.language,
        street: data.street,
        telephoneNumber: data.telephoneNumber,
        zipcode: data.zipcode,
      };
      const contactType = {
        dob: data.dob
          ? moment(
              data.dob,
              CONSTANTS.DATE_FORMATS.DAY_MONTH_YEAR_FORMAT
            ).format(CONSTANTS.DATE_FORMATS.YEAR_MONTH_DAY_HYPHEN_FORMAT)
          : "",
        firstName: data.firstName,
        gender: data.gender,
        lastName: data.lastName,
        salutation: data.salutation,
      };
      const apiRequest: UserApiRequest = {
        email: data.email,
        profile:
          data.contactType === CONTACT_TYPE.COMPANY_WITHOUT_CONTACT
            ? commonContactType
            : { ...commonContactType, ...contactType },
        recaptcha: {
          token: tempToken ?? "",
          version: useV2Recaptcha
            ? CONSTANTS.RECAPTCHA_VERSION.V2
            : CONSTANTS.RECAPTCHA_VERSION.V3,
        },
        ...(invitationToken !== "" && { isInvitation: true }),
      };
      API_SERVICES.USER.GET_USER_DETAILS(apiRequest).then(response => {
        if (
          response.status === CONSTANTS.HTTP_STATUS.INVALID_DATA &&
          (response?.data?.message ===
            CONSTANTS.RECAPTCHA_INVALID_MESSAGE.INVALID ||
            response?.data?.message ===
              CONSTANTS.RECAPTCHA_INVALID_MESSAGE.EMPTY)
        ) {
          if (!useV2Recaptcha) {
            setUseV2Recaptcha(true);
            setToken("");
          }

          setIsLoading(false);
          return;
        }

        const responseData = handleResponse(response);

        setIsLoading(false);
        if (responseData?.ok) {
          if (invitationToken == "") {
            navigate("/");
            commonUtils.toastService(
              t("registrationSuccessMessage"),
              CONSTANTS.TOAST_TYPE.SUCCESS
            );
          } else {
            commonUtils.toastService(
              t("invitedRegistrationSuccessMessage"),
              CONSTANTS.TOAST_TYPE.SUCCESS
            );
          }
          dispatch(authSlice.actions.setInvitationProfileId(""));
          dispatch(modalSlice.actions.updateRegisterModal(false));
        }
        if (invitationToken != "") {
          dispatch(authSlice.actions.invitationCode(invitationToken ?? ""));
          dispatch(authSlice.actions.setInvitationProfileId(""));
          dispatch(modalSlice.actions.updatePasswordModal(true));
        }
      });
    }
  };

  const formDataProps = {
    errors: errors as ErrorData,
    handleSubmit: handleSubmit(submitForm as SubmitHandler<FieldValues>),
    isSubmitButtonDisabled: isLoading,
    register: register,
    setValue: setValue,
  };

  return (
    <Modals
      flag={true}
      footerContent={<FooterModal />}
      footerClassName="fg-modal-footer"
      onClose={(e: boolean) => {
        dispatch(modalSlice.actions.updateRegisterModal(e));
        dispatch(modalSlice.actions.updateAuthModal(e));
        if (isInvitation) {
          navigate("/");
        }
      }}
      isModalHeader={false}
      modalClassName="registrationModal"
    >
      <div
        className="container-fluid fg-z-index-1 position-relative"
        data-testid="register-header"
      >
        <div className="fg-logo-within-shape">
          <img
            src={AVAILIO_LOGO}
            className="fg-logo-img"
            alt={t("availioTitle")}
          />
        </div>
      </div>
      <div className="container">
        <Loader isLoading={isLoading} />

        <div className="row justify-content-center">
          <div className="col-sm-12 col-md-12 col-xl-10">
            <PageTitle title={t("register")}></PageTitle>
            <div className="alert alert-primary fg-FS-14" role="alert">
              {t("registerSubheading")}
            </div>
            <CustomForm
              key={formKey}
              activeFields={activeFields}
              control={control}
              formData={formDataProps}
              isInvitation={isInvitation}
              enableRecaptcha={true}
              recaptchaConfig={{
                onV2RecaptchaChange: (token: string) => setToken(token),
                gToken: token,
                recaptchaV2Ref,
                recaptchaV3Ref,
                useV2Recaptcha,
              }}
              registerOptions={registerOptions}
            />
          </div>
        </div>
      </div>
    </Modals>
  );
};

export default RegisterModal;
